/**
 * VersionHistory component.
 * Displays a list of configuration versions with status, timestamps, and actions.
 * Supports viewing, comparing, and rolling back versions.
 */

import { useCallback, useState } from "react";

import type { VersionHistoryEntry } from "../diff/types";

import type { VersionHistoryProps } from "./types";

/**
 * VersionHistory component for viewing and managing config versions.
 */
export function VersionHistory({
  verticalKey,
  configType,
  entries,
  isLoading,
  liveVersion,
  onViewVersion,
  onCompareVersions,
  onRollback,
  pagination,
  onLoadMore,
}: VersionHistoryProps): React.ReactElement {
  const [compareFrom, setCompareFrom] = useState<number | null>(null);

  // Handle version selection for comparison
  const handleSelectForCompare = useCallback(
    (version: number): void => {
      if (compareFrom === null) {
        setCompareFrom(version);
      } else if (compareFrom === version) {
        setCompareFrom(null);
      } else {
        onCompareVersions(compareFrom, version);
        setCompareFrom(null);
      }
    },
    [compareFrom, onCompareVersions]
  );

  // Cancel comparison mode
  const handleCancelCompare = useCallback((): void => {
    setCompareFrom(null);
  }, []);

  return (
    <div className="version-history" data-testid="version-history">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Version History
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {verticalKey} / {configType}
          </p>
        </div>
        {compareFrom !== null && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-600 dark:text-blue-400">
              Select another version to compare with v{compareFrom}
            </span>
            <button
              type="button"
              onClick={handleCancelCompare}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Version list */}
      <div className="space-y-2">
        {isLoading && entries.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <SpinnerIcon className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <HistoryIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No version history available</p>
          </div>
        ) : (
          entries.map((entry) => (
            <VersionHistoryItem
              key={entry.version}
              entry={entry}
              isLive={entry.version === liveVersion}
              isCompareSource={compareFrom === entry.version}
              compareMode={compareFrom !== null}
              onView={onViewVersion}
              onCompare={handleSelectForCompare}
              onRollback={onRollback}
            />
          ))
        )}
      </div>

      {/* Load more button */}
      {pagination?.hasMore && onLoadMore && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400
                       disabled:opacity-50 flex items-center gap-2 mx-auto"
          >
            {isLoading && <SpinnerIcon className="w-4 h-4 animate-spin" />}
            {isLoading ? "Loading..." : "Load More"}
          </button>
          <p className="text-xs text-gray-500 mt-1">
            Showing {entries.length} of {pagination.total} versions
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface VersionHistoryItemProps {
  readonly entry: VersionHistoryEntry;
  readonly isLive: boolean;
  readonly isCompareSource: boolean;
  readonly compareMode: boolean;
  readonly onView: (version: number) => void;
  readonly onCompare: (version: number) => void;
  readonly onRollback: (version: number) => void;
}

function VersionHistoryItem({
  entry,
  isLive,
  isCompareSource,
  compareMode,
  onView,
  onCompare,
  onRollback,
}: VersionHistoryItemProps): React.ReactElement {
  const {
    version,
    versionString,
    status,
    changeSummary,
    createdAt,
    createdBy,
    promotedAt,
    promotedBy,
  } = entry;

  const canRollback = !isLive && status !== "draft";

  return (
    <div
      className={`p-4 rounded-lg border ${
        isLive
          ? "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800"
          : isCompareSource
            ? "bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800"
            : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
      }`}
      data-testid={`version-${version}`}
    >
      <div className="flex items-start justify-between">
        {/* Version info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {versionString || `v${version}`}
            </span>
            <StatusBadge status={status} isLive={isLive} />
          </div>

          {changeSummary && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
              {changeSummary}
            </p>
          )}

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            <span>
              Created {formatDate(createdAt)} by {createdBy}
            </span>
            {promotedAt && (
              <span>
                Promoted {formatDate(promotedAt)} by {promotedBy}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-4">
          <button
            type="button"
            onClick={() => onView(version)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            title="View version"
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onCompare(version)}
            className={`p-2 rounded-lg ${
              isCompareSource
                ? "text-blue-600 bg-blue-100 dark:bg-blue-900/30"
                : compareMode
                  ? "text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            }`}
            title={isCompareSource ? "Selected for comparison" : "Compare"}
          >
            <CompareIcon className="w-4 h-4" />
          </button>
          {canRollback && (
            <button
              type="button"
              onClick={() => onRollback(version)}
              className="p-2 text-amber-500 hover:text-amber-600 dark:hover:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20"
              title="Rollback to this version"
            >
              <RollbackIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface StatusBadgeProps {
  readonly status: VersionHistoryEntry["status"];
  readonly isLive: boolean;
}

function StatusBadge({ status, isLive }: StatusBadgeProps): React.ReactElement {
  const colors = {
    live: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    draft: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    archived: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
  };

  const displayStatus = isLive ? "live" : status;

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[displayStatus]}`}>
      {displayStatus}
    </span>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) {
    return "just now";
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }
}

// ============================================================================
// Icons
// ============================================================================

function SpinnerIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function HistoryIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function EyeIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

function CompareIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

function RollbackIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
      />
    </svg>
  );
}

export default VersionHistory;
